import { AREA_FLOOR, HERO_FLOOR, applyScrim } from "@/lib/adaptiveScrim";
import { pictureSources, type StoredImage } from "@/lib/imageSet";

/* The photograph behind a headline — the hero's, and an area tile's.
 *
 * ONE COMPONENT FOR BOTH, because the rule that matters is the same in both
 * places: the words on top have to stay readable on a photograph nobody has
 * seen yet. The scrim is therefore not the caller's business and not
 * optional — it is part of this component, so there is no way to place one of
 * these pictures without it.
 *
 * WHY AN <img> AND NOT A BACKGROUND. `background-image` cannot carry a
 * `srcset`, so a phone downloaded the same 2400px photo a desktop did. This
 * serves the width the screen actually needs, WebP where the browser takes it
 * and JPEG everywhere else.
 *
 * `sizes` is the caller's, because only the caller knows how wide the box is:
 * the hero is the full viewport, an area tile is a quarter of it.
 */
/* Which scrim this picture sits under, and therefore what has to be measured.
 *
 * "area"  the name runs across the bottom of the tile, so only the bottom
 *         third of the photograph matters; the variable goes on the tile,
 *         because the scrim is the tile's ::before.
 * "hero"  the headline sits in the middle band, and the scrim is its own
 *         element.
 * undefined — a picture with no words on it needs no scrim and no measuring. */
export type ScrimKind = "area" | "hero";

/* Find the element the custom property has to land on, from the <img>. */
function scrimTargetFor(img: HTMLImageElement, kind: ScrimKind): HTMLElement | null {
  if (kind === "area") return img.closest(".sq-area-tile");
  return img.closest(".sq-hero")?.querySelector<HTMLElement>(".sq-hero__scrim") ?? null;
}

export function BackdropImage({
  image, sizes, className, alt = "", priority = false, scrim,
}: {
  image: StoredImage;
  sizes: string;
  className: string;
  /** Measure this photograph and strengthen its scrim if it needs it. */
  scrim?: ScrimKind;
  /** The hero is the page's largest paint; an area tile is far below the
   *  fold. Lazy-loading the first one delays exactly the pixel the page is
   *  measured on, so the hero asks for priority and nothing else does. */
  priority?: boolean;
  /* Decorative by default. These pictures are atmosphere behind a heading
     that already says the place; describing them again would make a screen
     reader read the same thing twice. */
  alt?: string;
}) {
  const p = pictureSources(image, sizes);
  if (!p) return null;

  return (
    <picture>
      {p.webpSrcSet && <source type="image/webp" srcSet={p.webpSrcSet} sizes={p.sizes} />}
      {p.jpegSrcSet && <source type="image/jpeg" srcSet={p.jpegSrcSet} sizes={p.sizes} />}
      <img
        className={className}
        src={p.src}
        width={p.width}
        height={p.height}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        decoding="async"
        /* Required to READ the pixels: without it the canvas is tainted and
         * getImageData throws, which would send every picture down the
         * fallback path. The storage bucket answers
         * `Access-Control-Allow-Origin: *`, so this costs nothing. Only set
         * when something is actually going to measure. */
        crossOrigin={scrim ? "anonymous" : undefined}
        onLoad={scrim ? (e) => {
          const img = e.currentTarget;
          const target = scrimTargetFor(img, scrim);
          /* Never a reason to break a page. A picture that cannot be measured
           * keeps the design's floor, which is what shipped before this. */
          if (target) try { applyScrim(img, target, scrim === "area" ? "area-name" : "text-box",
            scrim === "area" ? AREA_FLOOR : HERO_FLOOR); } catch { /* floor stands */ }
        } : undefined}
      />
    </picture>
  );
}
